<template>
  <div v-if="visible" data-testid="payroll-card">
    <v-row class="mb-2" align="center">
      <v-col cols="12" sm="6">
        <h2>
          Payroll
          <v-chip v-if="report" size="small" :color="stateColor" class="ml-2" data-testid="payroll-state">{{ stateLabel }}</v-chip>
        </h2>
        <p class="text-caption text-medium-emphasis">What the crew is owed, from approved time. Manager-only.</p>
      </v-col>
    </v-row>

    <v-alert v-if="error || store.error" type="error" closable class="mb-3" @click:close="dismissError">
      {{ error || store.error }}
    </v-alert>

    <!-- Nothing generated yet -->
    <v-card v-if="!report">
      <v-card-text>
        <p class="text-body2 mb-3">No payroll report yet.</p>
        <v-alert v-if="blockers.length" type="warning" variant="tonal" density="compact" class="mb-3">
          <div v-for="b in blockers" :key="b">{{ b }}</div>
        </v-alert>
        <v-btn color="primary" :disabled="blockers.length > 0" :loading="store.busy" data-testid="generate-payroll" @click="generate">
          Generate payroll report
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card v-else>
      <v-card-text>
        <v-alert v-if="report.state === 'DRAFT' && report.reopenReason" type="info" variant="tonal" density="compact" class="mb-3">
          Reopened: {{ report.reopenReason }}
        </v-alert>
        <v-alert v-if="draftStale" type="warning" variant="tonal" density="compact" class="mb-3" data-testid="payroll-stale">
          Approved time has changed since this draft was generated — regenerate it before finalizing.
        </v-alert>
        <v-alert v-if="report.state === 'DRAFT' && blockers.length" type="warning" variant="tonal" density="compact" class="mb-3">
          <div v-for="b in blockers" :key="b">{{ b }}</div>
        </v-alert>

        <v-table density="compact">
          <thead>
            <tr>
              <th>Person</th>
              <th>Rate</th>
              <th class="text-right">Worked h</th>
              <th class="text-right">Gross pay</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="e in report.entries" :key="e.bookingId">
              <tr>
                <td>
                  {{ e.name }}<v-chip v-if="e.paid" size="x-small" color="success" class="ml-2">Paid</v-chip>
                  <div class="text-caption text-medium-emphasis">{{ e.role }}<template v-if="e.label"> — {{ e.label }}</template>
                    <v-chip v-if="e.sharedWith?.length" size="x-small" color="primary" variant="tonal" class="ml-1" data-testid="payroll-shared">Shared day with {{ e.sharedWith.join(', ') }}</v-chip></div>
                </td>
                <td>${{ e.rate.toLocaleString() }}/{{ e.rateType === 'hourly' ? 'hr' : 'day' }}</td>
                <td class="text-right">{{ e.workedHours.toFixed(2) }}</td>
                <td class="text-right">${{ money(e.grossPay) }}</td>
                <td class="text-right">
                  <v-btn size="x-small" variant="text" @click="toggle(e.bookingId)">{{ open.has(e.bookingId) ? 'Hide days' : 'Days' }}</v-btn>
                </td>
              </tr>
              <tr v-for="d in open.has(e.bookingId) ? e.days : []" :key="e.bookingId + d.date" class="text-caption">
                <td class="pl-8">{{ d.date }}</td>
                <td>
                  <span v-if="d.holiday">Holiday </span><span v-if="d.shortRest">Short rest</span>
                </td>
                <td class="text-right">{{ d.billedHours.toFixed(2) }} billed<template v-if="d.ot1Hours || d.ot2Hours"> ({{ (d.ot1Hours + d.ot2Hours).toFixed(2) }} OT)</template></td>
                <td class="text-right">${{ money(d.cost) }}</td>
                <td></td>
              </tr>
            </template>
            <tr v-for="f in report.fees" :key="f.bookingId + 'fee'">
              <td>
                {{ f.name }}<v-chip v-if="f.paid" size="x-small" color="success" class="ml-2">Paid</v-chip>
                <div class="text-caption text-medium-emphasis">Cancellation fee — {{ f.role }}</div>
              </td>
              <td colspan="2" class="text-caption text-medium-emphasis">{{ f.reason }}</td>
              <td class="text-right">${{ money(f.amount) }}</td>
              <td></td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th colspan="3" class="text-right">Total</th>
              <th class="text-right" data-testid="payroll-total">${{ money(report.totalCost) }}</th>
              <th></th>
            </tr>
          </tfoot>
        </v-table>

        <v-alert v-if="report.warnings.length" type="info" variant="tonal" density="compact" class="mt-3">
          <div v-for="w in report.warnings" :key="w">{{ w }}</div>
        </v-alert>

        <p v-if="report.state === 'PAID'" class="text-body2 mt-3 mb-0">
          Paid {{ formatDate(report.paidAt) }}<template v-if="report.paymentReference"> — {{ report.paymentReference }}</template>.
        </p>
        <p v-else-if="report.state === 'PROCESSED'" class="text-caption text-medium-emphasis mt-3 mb-0">
          Finalized {{ formatDate(report.processedAt) }}.
          <template v-if="anyPaid">
            <strong data-testid="partly-paid">Partly paid — ${{ money(paidTotals.paid) }} of ${{ money(report.totalCost) }}.</strong>
            Reopening is off once any payment has gone out; pay the rest per person or all at once.
          </template>
          <template v-else>This gig's timesheets are locked until it's reopened.</template>
        </p>
        <p class="text-caption text-medium-emphasis mt-2 mb-0">Version {{ report.version }} · generated {{ formatDate(report.generatedAt) }}</p>
      </v-card-text>

      <v-card-actions class="flex-wrap">
        <v-btn size="small" variant="outlined" @click="store.downloadCsv(report, gig.name)">CSV</v-btn>
        <v-btn
          v-if="report.state !== 'DRAFT'"
          size="small"
          variant="outlined"
          :loading="store.busy"
          data-testid="download-pdf"
          @click="downloadPdf"
        >
          PDF
        </v-btn>
        <v-spacer />
        <template v-if="report.state === 'DRAFT'">
          <v-btn size="small" variant="outlined" :loading="store.busy" data-testid="regenerate-payroll" @click="generate">Regenerate</v-btn>
          <v-btn
            size="small"
            color="primary"
            variant="flat"
            :disabled="draftStale || blockers.length > 0"
            data-testid="finalize-payroll"
            @click="finalizeOpen = true"
          >
            Finalize…
          </v-btn>
        </template>
        <template v-else-if="report.state === 'PROCESSED'">
          <v-btn v-if="!anyPaid" size="small" variant="outlined" data-testid="reopen-payroll" @click="openReopen">Reopen…</v-btn>
          <v-btn size="small" color="primary" variant="flat" data-testid="mark-paid" @click="openPaid">{{ anyPaid ? 'Mark remaining paid…' : 'Mark paid…' }}</v-btn>
        </template>
      </v-card-actions>
    </v-card>

    <!-- Finalize -->
    <v-dialog v-model="finalizeOpen" max-width="440">
      <v-card>
        <v-card-title>Finalize payroll</v-card-title>
        <v-card-text>
          <p class="text-body2">
            This saves the report (${{ report ? money(report.totalCost) : '0.00' }}), produces the PDF, and <strong>locks this gig's
            timesheets</strong> so the numbers can't drift. You can reopen it later if something was wrong, until it's marked paid.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="finalizeOpen = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :loading="store.busy" data-testid="confirm-finalize" @click="finalize">Finalize</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Mark paid -->
    <v-dialog v-model="paidOpen" max-width="420">
      <v-card>
        <v-card-title>Mark payroll paid</v-card-title>
        <v-card-text>
          <v-text-field v-model="paidDate" type="date" label="Date paid" />
          <v-text-field v-model="paidRef" label="Reference (optional)" hint="Check number, ACH batch, etc." persistent-hint maxlength="200" data-testid="paid-reference" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="paidOpen = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :loading="store.busy" data-testid="confirm-paid" @click="markPaid">Mark paid</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reopen -->
    <v-dialog v-model="reopenOpen" max-width="440">
      <v-card>
        <v-card-title>Reopen payroll</v-card-title>
        <v-card-text>
          <p class="text-body2 mb-3">The PDF is removed and timesheets unlock so you can correct them; then regenerate and finalize again.</p>
          <v-textarea v-model="reopenReason" label="Reason (required)" rows="2" auto-grow maxlength="1000" data-testid="reopen-reason" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="reopenOpen = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :disabled="!reopenReason.trim()" :loading="store.busy" data-testid="confirm-reopen" @click="reopen">Reopen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import type { ActualsSummary, Gig } from '@gigdriver/domain';
import { hasAnyPaidLine, reportPaidTotals } from '@gigdriver/domain';
import { usePayrollStore } from '../stores/payroll';

const props = defineProps<{ orgId: string; gig: Gig; actuals: ActualsSummary }>();
const store = usePayrollStore();
const report = computed(() => store.report);

onMounted(() => store.watchGig(props.orgId, props.gig.id));
watch(() => [props.orgId, props.gig.id], ([o, g]) => store.watchGig(o, g));
onUnmounted(() => store.stopGigWatch());

const error = ref<string | null>(null);

// Hidden until there's something to do: payroll starts once the gig is
// completed (or already has a report).
const visible = computed(() => ['COMPLETED', 'INVOICED', 'PAID'].includes(props.gig.state) || !!report.value);

// The client-side view of what blocks generating/finalizing (the server
// re-checks everything): unreviewed time on the gig.
const blockers = computed(() => {
  const out: string[] = [];
  const { openCount, pendingCount } = props.actuals.totals;
  if (openCount > 0) out.push(`${openCount} ${openCount === 1 ? 'person is' : 'people are'} still clocked in.`);
  if (pendingCount > 0) out.push(`${pendingCount} timesheet ${pendingCount === 1 ? 'entry still needs' : 'entries still need'} review.`);
  return out;
});

const anyPaid = computed(() => !!report.value && hasAnyPaidLine(report.value));
const paidTotals = computed(() => (report.value ? reportPaidTotals(report.value) : { paid: 0, unpaid: 0 }));

const feesTotal = computed(() => (report.value?.fees ?? []).reduce((t, f) => t + f.amount, 0));
const draftStale = computed(
  () => report.value?.state === 'DRAFT' && Math.round((report.value.totalCost - feesTotal.value) * 100) !== Math.round(props.actuals.totals.approvedCost * 100),
);

const stateLabel = computed(() => ({ DRAFT: 'Draft', PROCESSED: 'Finalized', PAID: 'Paid' })[report.value?.state ?? 'DRAFT']);
const stateColor = computed(() => ({ DRAFT: 'warning', PROCESSED: 'primary', PAID: 'success' })[report.value?.state ?? 'DRAFT']);

const open = ref(new Set<string>());
function toggle(id: string) {
  const next = new Set(open.value);
  if (!next.delete(id)) next.add(id);
  open.value = next;
}

async function generate() {
  try {
    await store.generate(props.orgId, props.gig.id);
  } catch {
    // surfaced via store.error
  }
}

const finalizeOpen = ref(false);
async function finalize() {
  try {
    await store.finalize(props.orgId, props.gig.id);
    finalizeOpen.value = false;
  } catch {
    finalizeOpen.value = false;
  }
}

const paidOpen = ref(false);
const paidDate = ref('');
const paidRef = ref('');
function openPaid() {
  const d = new Date();
  paidDate.value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  paidRef.value = '';
  paidOpen.value = true;
}
async function markPaid() {
  try {
    // Local noon avoids a time-zone edge shifting the calendar day.
    const when = paidDate.value ? new Date(`${paidDate.value}T12:00:00`) : undefined;
    await store.markPaid(props.orgId, props.gig.id, when, paidRef.value.trim() || undefined);
    paidOpen.value = false;
  } catch {
    paidOpen.value = false;
  }
}

const reopenOpen = ref(false);
const reopenReason = ref('');
function openReopen() {
  reopenReason.value = '';
  reopenOpen.value = true;
}
async function reopen() {
  try {
    await store.reopen(props.orgId, props.gig.id, reopenReason.value.trim());
    reopenOpen.value = false;
  } catch {
    reopenOpen.value = false;
  }
}

async function downloadPdf() {
  if (!report.value) return;
  try {
    await store.downloadPdf(props.orgId, props.gig.id, props.gig.name, report.value.version);
  } catch {
    // surfaced via store.error
  }
}

function dismissError() {
  error.value = null;
  store.error = null;
}

function money(n: number): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function formatDate(d?: Date): string {
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}
</script>
